<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">Network Performance</span>
                </h1>
                <p class="text-muted mb-3">Real-time Solana network metrics for developers and validators</p>
                <div class="d-flex align-items-center gap-3 flex-wrap">
                    <date-range-picker default-range="24h" @change="handleDateRangeChange" />
                    <granularity-toggle
                        v-model="granularityMode"
                        :interval="currentInterval"
                        @change="handleGranularityChange"
                    />
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Avg TPS</div>
                <div class="hero-value">{{ summary.avgTps }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Completion Rate</div>
                <div class="hero-value">{{ summary.completionRate }}%</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Revert Rate</div>
                <div class="hero-value">{{ summary.revertRate }}%</div>
                <div class="hero-subtitle">intentional cancellations</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Total Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransactions) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">CU Utilization</div>
                <div class="hero-value">{{ summary.computeUtilization }}%</div>
            </div>
            <div class="analytics-hero-stat">
                <div class="hero-label">Jito Bundles</div>
                <div class="hero-value">{{ summary.jitoPercent }}%</div>
            </div>
        </div>

        <!-- SECTION: Real-time TPS -->
        <div class="section-divider">
            <span class="section-title">Real-time Performance</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">TPS Over Time</div>
                    <div class="panel-subtitle">Transactions per second</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="tpsChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Completion Rate</div>
                    <div class="panel-subtitle">Completed vs Reverted transactions</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="completionChart"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Fee Market -->
        <div class="section-divider">
            <span class="section-title">Fee Market</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Breakdown</div>
                    <div class="panel-subtitle">Base + Priority + Jito Tips</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feesChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Composition</div>
                </div>
                <div class="panel-body">
                    <div class="fee-breakdown-bar">
                        <div class="fee-segment base" :style="{ width: baseFeePercent + '%' }">Base</div>
                        <div class="fee-segment priority" :style="{ width: priorityFeePercent + '%' }">Priority</div>
                        <div class="fee-segment tips" :style="{ width: tipsFeePercent + '%' }">Tips</div>
                    </div>
                    <div class="fee-totals mt-3">
                        <div class="fee-total-row">
                            <span class="fee-dot base"></span>
                            <span class="fee-label">Base Fees</span>
                            <span class="fee-value">{{ formatSOL(summary.totalBaseFees) }}</span>
                        </div>
                        <div class="fee-total-row">
                            <span class="fee-dot priority"></span>
                            <span class="fee-label">Priority Fees</span>
                            <span class="fee-value">{{ formatSOL(summary.totalPriorityFees) }}</span>
                        </div>
                        <div class="fee-total-row">
                            <span class="fee-dot tips"></span>
                            <span class="fee-label">Jito Tips</span>
                            <span class="fee-value">{{ formatSOL(summary.totalJitoTips) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fee Percentiles -->
        <div class="analytics-row cols-3 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Total Fee Percentiles</div>
                </div>
                <div class="panel-body">
                    <div class="percentile-grid">
                        <div class="percentile-item">
                            <div class="percentile-label">p50</div>
                            <div class="percentile-value">{{ formatSOL(summary.feeP50) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p90</div>
                            <div class="percentile-value">{{ formatSOL(summary.feeP90) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p99</div>
                            <div class="percentile-value">{{ formatSOL(summary.feeP99) }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Priority Fee Percentiles</div>
                    <div class="panel-subtitle">lamports</div>
                </div>
                <div class="panel-body">
                    <div class="percentile-grid">
                        <div class="percentile-item">
                            <div class="percentile-label">p50</div>
                            <div class="percentile-value">{{ formatCompact(summary.priorityP50) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p90</div>
                            <div class="percentile-value">{{ formatCompact(summary.priorityP90) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p99</div>
                            <div class="percentile-value">{{ formatCompact(summary.priorityP99) }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Jito Tip Percentiles</div>
                </div>
                <div class="panel-body">
                    <div class="percentile-grid">
                        <div class="percentile-item">
                            <div class="percentile-label">p50</div>
                            <div class="percentile-value">{{ formatSOL(summary.jitoTipP50) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p90</div>
                            <div class="percentile-value">{{ formatSOL(summary.jitoTipP90) }}</div>
                        </div>
                        <div class="percentile-item">
                            <div class="percentile-label">p99</div>
                            <div class="percentile-value">{{ formatSOL(summary.jitoTipP99) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Completed vs Reverted -->
        <div class="section-divider">
            <span class="section-title">Transaction Completion Analysis</span>
        </div>
        <div class="analytics-row cols-3 mb-4">
            <div class="analytics-panel">
                <div class="panel-body text-center py-4">
                    <div class="completion-stat completed">
                        <div class="completion-value">{{ formatCompact(completion.completed?.count || 0) }}</div>
                        <div class="completion-label">Completed</div>
                        <div class="completion-percent">{{ completion.completed?.percent || 0 }}%</div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-4">
                    <div class="completion-stat reverted">
                        <div class="completion-value">{{ formatCompact(completion.reverted?.count || 0) }}</div>
                        <div class="completion-label">Reverted</div>
                        <div class="completion-percent">{{ completion.reverted?.percent || 0 }}%</div>
                    </div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body">
                    <div class="completion-comparison">
                        <div class="comparison-row">
                            <span class="comparison-label">Avg Fee (Completed)</span>
                            <span class="comparison-value">{{ formatSOL(completion.completed?.avgFee || 0) }}</span>
                        </div>
                        <div class="comparison-row">
                            <span class="comparison-label">Avg Fee (Reverted)</span>
                            <span class="comparison-value">{{ formatSOL(completion.reverted?.avgFee || 0) }}</span>
                        </div>
                        <div class="comparison-row">
                            <span class="comparison-label">Compute (Completed)</span>
                            <span class="comparison-value">{{ formatCompact(completion.completed?.compute || 0) }}</span>
                        </div>
                        <div class="comparison-row">
                            <span class="comparison-label">Compute (Reverted)</span>
                            <span class="comparison-value">{{ formatCompact(completion.reverted?.compute || 0) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Native vs Jito Transactions -->
        <div class="section-divider">
            <span class="section-title">Native Transactions vs Jito</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Distribution</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="jitoChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Comparison</div>
                </div>
                <div class="panel-body">
                    <div class="jito-comparison">
                        <div class="jito-column native">
                            <div class="jito-header">Native Transactions</div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Transactions</span>
                                <span class="jito-metric-value">{{ formatCompact(jitoData.nonJito?.txCount || 0) }}</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Share</span>
                                <span class="jito-metric-value">{{ jitoData.nonJito?.percent || 0 }}%</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Total Fees</span>
                                <span class="jito-metric-value">{{ formatSOL(jitoData.nonJito?.fees || 0) }}</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">-</span>
                                <span class="jito-metric-value">-</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Avg Fee</span>
                                <span class="jito-metric-value">{{ formatSOL(jitoData.nonJito?.avgFee || 0) }}</span>
                            </div>
                        </div>
                        <div class="jito-column jito">
                            <div class="jito-header">Jito Bundles</div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Transactions</span>
                                <span class="jito-metric-value">{{ formatCompact(jitoData.jito?.txCount || 0) }}</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Share</span>
                                <span class="jito-metric-value">{{ jitoData.jito?.percent || 0 }}%</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Total Fees</span>
                                <span class="jito-metric-value">{{ formatSOL(jitoData.jito?.fees || 0) }}</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Total Tips</span>
                                <span class="jito-metric-value">{{ formatSOL(jitoData.jito?.tips || 0) }}</span>
                            </div>
                            <div class="jito-metric">
                                <span class="jito-metric-label">Avg Fee</span>
                                <span class="jito-metric-value">{{ formatSOL(jitoData.jito?.avgFee || 0) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Compute & Instructions -->
        <div class="section-divider">
            <span class="section-title">Compute & Instructions</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Units</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="computeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Instructions</div>
                    <div class="panel-subtitle">Total vs Inner (CPI)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="instructionsChart"></canvas>
                </div>
            </div>
        </div>

        <!-- Complexity metrics -->
        <div class="analytics-row cols-4 mb-4">
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="metric-value">{{ summary.instructionsPerTx }}</div>
                    <div class="metric-label">Instructions/Tx</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="metric-value">{{ summary.avgCpiDepth }}</div>
                    <div class="metric-label">Avg CPI Depth</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="metric-value">{{ summary.maxCpiDepth }}</div>
                    <div class="metric-label">Max CPI Depth</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="metric-value">{{ summary.computeUtilization }}%</div>
                    <div class="metric-label">CU Utilization</div>
                </div>
            </div>
        </div>

        <!-- SECTION: Raw Data -->
        <div class="section-divider">
            <span class="section-title">Time Series Data</span>
        </div>
        <div class="analytics-row cols-1">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Time Series Data</div>
                    <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportData">Export CSV</button>
                </div>
                <div class="panel-body">
                    <div class="table-responsive" style="max-height: 300px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th class="text-end">TPS</th>
                                    <th class="text-end">Completed</th>
                                    <th class="text-end">Reverted</th>
                                    <th class="text-end">Rate</th>
                                    <th class="text-end">Fees</th>
                                    <th class="text-end">Jito %</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in timeSeries.slice(-24)" :key="row.timestamp">
                                    <td>{{ row.label }}</td>
                                    <td class="text-end font-mono">{{ row.tps }}</td>
                                    <td class="text-end font-mono text-success">{{ formatCompact(row.completed) }}</td>
                                    <td class="text-end font-mono text-warning">{{ formatCompact(row.reverted) }}</td>
                                    <td class="text-end font-mono">{{ row.completionRate }}%</td>
                                    <td class="text-end font-mono">{{ formatSOL(row.fees) }}</td>
                                    <td class="text-end font-mono">{{ row.txCount > 0 ? ((row.jitoTxCount / row.txCount) * 100).toFixed(1) : 0 }}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colors, getLineChartOptions, getStackedBarChartOptions, getBaseOptions } from '../composables/useChartConfig';
import { GRANULARITY_MODES, transformValue, getUnitSuffix } from '../composables/useGranularityToggle';

export default {
    name: 'NetworkAnalytics',

    data() {
        return {
            currentRange: '24h',
            currentInterval: '1h',
            granularityMode: GRANULARITY_MODES.TOTAL,
            summary: {},
            timeSeries: [],
            completion: {},
            jitoData: {},
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    computed: {
        baseFeePercent() {
            const total = (this.summary.totalBaseFees || 0) + (this.summary.totalPriorityFees || 0) + (this.summary.totalJitoTips || 0);
            return total > 0 ? ((this.summary.totalBaseFees || 0) / total * 100).toFixed(1) : 0;
        },
        priorityFeePercent() {
            const total = (this.summary.totalBaseFees || 0) + (this.summary.totalPriorityFees || 0) + (this.summary.totalJitoTips || 0);
            return total > 0 ? ((this.summary.totalPriorityFees || 0) / total * 100).toFixed(1) : 0;
        },
        tipsFeePercent() {
            const total = (this.summary.totalBaseFees || 0) + (this.summary.totalPriorityFees || 0) + (this.summary.totalJitoTips || 0);
            return total > 0 ? ((this.summary.totalJitoTips || 0) / total * 100).toFixed(1) : 0;
        }
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.loadData();
        },

        handleGranularityChange() {
            this.updateCharts();
        },

        initCharts() {
            // Use design system chart configurations
            const lineOpts = getLineChartOptions({ enableZoom: true });
            const stackedOpts = getStackedBarChartOptions({});

            this.charts.tps = new Chart(this.$refs.tpsChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: lineOpts
            });

            this.charts.completion = new Chart(this.$refs.completionChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ enablePan: true })
            });

            this.charts.fees = new Chart(this.$refs.feesChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ formatCallback: v => this.formatSOL(v) })
            });

            this.charts.jito = new Chart(this.$refs.jitoChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({})
            });

            this.charts.compute = new Chart(this.$refs.computeChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ formatCallback: v => this.formatCompact(v) })
            });

            this.charts.instructions = new Chart(this.$refs.instructionsChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ formatCallback: v => this.formatCompact(v) })
            });
        },

        async loadData() {
            try {
                const [overview, jito, completionData] = await Promise.all([
                    fetch(`/analytics/api/network-overview?range=${this.currentRange}`).then(r => r.json()),
                    fetch(`/analytics/api/jito-comparison?range=${this.currentRange}`).then(r => r.json()),
                    fetch(`/analytics/api/completion-breakdown?range=${this.currentRange}`).then(r => r.json())
                ]);

                this.summary = overview.summary || {};
                this.timeSeries = overview.timeSeries || [];
                this.jitoData = jito;
                this.completion = completionData;

                this.updateCharts();
            } catch (e) {
                console.error('Error loading network data:', e);
            }
        },

        updateCharts() {
            const ts = this.timeSeries;
            const labels = ts.map(d => d.label);
            const transform = this.transformForGranularity.bind(this);

            // TPS chart - already a rate, no transformation needed
            this.charts.tps.data = {
                labels,
                datasets: [{
                    label: 'TPS',
                    data: ts.map(d => d.tps),
                    borderColor: '#10f7cf',
                    backgroundColor: 'rgba(16, 247, 207, 0.1)',
                    fill: true,
                    tension: 0.3
                }]
            };
            this.charts.tps.update();

            // Completion chart - transform transaction counts
            this.charts.completion.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Completed'), data: ts.map(d => transform(d.completed)), backgroundColor: 'rgba(34, 197, 94, 0.7)' },
                    { label: this.getGranularityLabel('Reverted'), data: ts.map(d => transform(d.reverted)), backgroundColor: 'rgba(251, 191, 36, 0.7)' }
                ]
            };
            this.charts.completion.update();

            // Fees chart - transform fee totals
            this.charts.fees.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Base Fees'), data: ts.map(d => transform(d.baseFees)), backgroundColor: 'rgba(99, 102, 241, 0.7)' },
                    { label: this.getGranularityLabel('Priority Fees'), data: ts.map(d => transform(d.priorityFees)), backgroundColor: 'rgba(139, 92, 246, 0.7)' },
                    { label: this.getGranularityLabel('Jito Tips'), data: ts.map(d => transform(d.jitoTips)), backgroundColor: 'rgba(249, 115, 22, 0.7)' }
                ]
            };
            this.charts.fees.update();

            // Native vs Jito chart - transform transaction counts
            const jitoTs = this.jitoData.timeSeries || [];
            this.charts.jito.data = {
                labels: jitoTs.map(d => d.label),
                datasets: [
                    { label: this.getGranularityLabel('Native'), data: jitoTs.map(d => transform(d.nonJitoCount)), backgroundColor: 'rgba(139, 92, 246, 0.7)' },
                    { label: this.getGranularityLabel('Jito'), data: jitoTs.map(d => transform(d.jitoCount)), backgroundColor: 'rgba(249, 115, 22, 0.7)' }
                ]
            };
            this.charts.jito.update();

            // Compute chart - transform compute units
            this.charts.compute.data = {
                labels,
                datasets: [{
                    label: this.getGranularityLabel('Compute Units'),
                    data: ts.map(d => transform(d.compute)),
                    borderColor: '#8b5cf6',
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    fill: true,
                    tension: 0.3
                }]
            };
            this.charts.compute.update();

            // Instructions chart - transform instruction counts
            this.charts.instructions.data = {
                labels,
                datasets: [
                    { label: this.getGranularityLabel('Total'), data: ts.map(d => transform(d.instructions)), borderColor: '#3b82f6' },
                    { label: this.getGranularityLabel('Inner (CPI)'), data: ts.map(d => transform(d.innerInstructions)), borderColor: '#ec4899' }
                ]
            };
            this.charts.instructions.update();
        },

        exportData() {
            const csv = [['Time', 'TPS', 'Completed', 'Reverted', 'Rate', 'Fees', 'Jito%'].join(',')];
            this.timeSeries.forEach(r => {
                const jitoPercent = r.txCount > 0 ? ((r.jitoTxCount / r.txCount) * 100).toFixed(1) : 0;
                csv.push([r.label, r.tps, r.completed, r.reverted, r.completionRate, r.fees, jitoPercent].join(','));
            });
            const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `network_${this.currentRange}.csv`;
            a.click();
        },

        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e12) return (v / 1e12).toFixed(2) + 'T';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return v.toLocaleString();
        },

        formatSOL(v) {
            if (!v) return '0 SOL';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M SOL';
            if (v >= 1e3) return (v / 1e3).toFixed(2) + 'K SOL';
            if (v >= 1) return v.toFixed(2) + ' SOL';
            return v.toFixed(6) + ' SOL';
        },

        transformForGranularity(value) {
            return transformValue(value, this.granularityMode, this.currentInterval);
        },

        getGranularityLabel(baseLabel) {
            const suffix = getUnitSuffix(this.granularityMode, this.currentInterval, '');
            return suffix ? `${baseLabel} ${suffix}` : baseLabel;
        }
    }
};
</script>

<style scoped>
/* Minimal component-specific styles - all common styles now in _compass-analytics.scss */

/* Hero stat cyan variant (specific to this dashboard) */
.hero-stat--cyan {
    background: linear-gradient(135deg, rgba(16, 247, 207, 0.08), rgba(20, 184, 166, 0.04));
    border-color: rgba(16, 247, 207, 0.15);
}

.hero-stat--cyan .hero-value {
    background: linear-gradient(135deg, #10f7cf, #14b8a6);
    -webkit-background-clip: text;
    background-clip: text;
}

/* Comparison value styling (specific font) */
.comparison-value {
    font-family: 'JetBrains Mono', monospace;
}
</style>
